// ── PolyAccountStore — Zustand store for Polymarket account state ──
//
// Mirrors accountStore pattern. Separate from marketStore (Hydra pipeline).
// Reuses V1 client.ts functions for auth, trading, and localStorage.

import { create } from 'zustand';
import type { ClobClient } from '@polymarket/clob-client';
import {
  loadPolyUserConfig,
  savePolyUserConfig,
  clearPolyUserConfig,
  createOrDerivePolyApiCreds,
  createPolyTradingClient,
  normalizePrivateKey,
  type PolyUserConfig,
} from '../lib/polymarket/client';
import type { PolyOpenOrder } from '../lib/polymarket/types';

// ── Module-level state (not in Zustand — mutable refs) ──────────

let _polyClient: ClobClient | null = null;

// ── Store ───────────────────────────────────────────────────────

export type PolyAccountStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface PolyAccountState {
  config: PolyUserConfig | null;
  status: PolyAccountStatus;
  error: string | null;
  openOrders: PolyOpenOrder[];

  connect: (args: { privateKey: string; signatureType: 0 | 1 | 2; funder?: string }) => Promise<void>;
  disconnect: () => void;
  removeCredentials: () => void;
  refreshOpenOrders: () => Promise<void>;
  getClient: () => ClobClient | null;
}

export const usePolyAccountStore = create<PolyAccountState>((set, get) => ({
  config: null,
  status: 'disconnected',
  error: null,
  openOrders: [],

  // ── Connect ─────────────────────────────────────────────────
  connect: async (args) => {
    if (get().status === 'connecting') return;
    set({ status: 'connecting', error: null });

    try {
      const privateKey = normalizePrivateKey(args.privateKey);
      if (!privateKey) throw new Error('Private key is required.');

      // 1. Derive API credentials (lazy-loads ethers + clob-client)
      const apiCreds = await createOrDerivePolyApiCreds(privateKey);

      // 2. Build and persist config
      const config: PolyUserConfig = {
        privateKey,
        signatureType: args.signatureType,
        funder: args.funder,
        apiCreds,
      };
      savePolyUserConfig(config);

      // 3. Create and validate trading client
      _polyClient = await createPolyTradingClient(config);

      set({ status: 'connected', error: null, config });
    } catch (err) {
      _polyClient = null;
      const message = err instanceof Error ? err.message : 'Polymarket connection failed';
      console.error('[PolyAccountStore] connect failed:', message);
      set({ status: 'error', error: message });
    }
  },

  // ── Disconnect ──────────────────────────────────────────────
  disconnect: () => {
    _polyClient = null;
    set({
      status: 'disconnected',
      error: null,
      openOrders: [],
    });
  },

  // ── Remove Credentials (disconnect + clear storage) ─────────
  removeCredentials: () => {
    get().disconnect();
    clearPolyUserConfig();
    set({ config: null });
  },

  // ── Refresh Open Orders ─────────────────────────────────────
  refreshOpenOrders: async () => {
    if (!_polyClient) return;
    try {
      const maybe = _polyClient as unknown as {
        getOpenOrders?: () => Promise<unknown>;
      };
      const raw = await maybe.getOpenOrders?.();
      if (!Array.isArray(raw)) return;

      const n = (v: unknown) => {
        const x = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
        return Number.isFinite(x) ? x : 0;
      };

      set({
        openOrders: raw.map((o) => {
          const rec: Record<string, unknown> =
            o && typeof o === 'object' ? (o as Record<string, unknown>) : {};
          return {
            id: String(rec.id ?? rec.orderID ?? rec.orderId ?? ''),
            assetId: String(rec.asset_id ?? rec.assetId ?? ''),
            conditionId: String(rec.market ?? rec.conditionId ?? ''),
            side: String(rec.side ?? ''),
            price: n(rec.price),
            originalSize: n(rec.original_size ?? rec.size ?? rec.originalSize),
            sizeMatched: n(rec.size_matched ?? rec.sizeMatched),
            timestampMs: rec.timestamp ? n(rec.timestamp) * 1000 : undefined,
          };
        }),
      });
    } catch (err) {
      console.warn('[PolyAccountStore] refreshOpenOrders failed:', err);
    }
  },

  // ── Get Client (for trading operations) ─────────────────────
  getClient: () => _polyClient,
}));

// ── Auto-reconnect helper (used by usePolyAccount hook) ────────

export async function autoConnectPoly(): Promise<void> {
  const saved = loadPolyUserConfig();
  if (!saved?.apiCreds || !saved.privateKey) return;

  await usePolyAccountStore.getState().connect({
    privateKey: saved.privateKey,
    signatureType: saved.signatureType,
    funder: saved.funder,
  });
}
